import { Component, Input, OnChanges, SimpleChanges, Renderer2, RendererFactory2, ViewEncapsulation } from '@angular/core';
import 'jquery-ui-dist/jquery-ui';
import { GlobalService } from 'app/global/global.service';
import { GeneralPopoverSettingsComponent } from '../general-popover-settings/general-popover-settings.component';
import { Block } from 'app/interfaces/blocks';
import { Subscription } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { FieldSettings } from '../../events/editor-emitter.dto';
import { SelectedRole } from '../general-popover-settings/general-popover-settings.dto';
import { BorderStyle, colorWithHeader, colorWithOutHeader, Theme } from './content-table-settings.dto';
import { Editor } from '@tiptap/core';
import { EventHandlersService } from '../../edit-template/event-handler.service';

@Component({
  selector: 'app-content-table-settings',
  templateUrl: './content-table-settings.component.html',
  styleUrls: ['./content-table-settings.component.scss'],
  providers: [GeneralPopoverSettingsComponent],
  encapsulation: ViewEncapsulation.None,
})
export class ContentTableSettingsComponent implements OnChanges {
  
  @Input() data: FieldSettings;
  private renderer: Renderer2;
  public timeOutSubscription: Subscription;
  public colorWithHeader:Array<Theme> = colorWithHeader;
  public colorWithOutHeader:Array<Theme> = colorWithOutHeader;
  public colors:Array<Theme> = [];
  public header:boolean = true;
  public footer:boolean = false;
  public headerColor: string = 'rgb(189, 189, 189)';
  public headerTextColor: string = '#000000';
  public evenColor: string = '#ffffff';
  public oddColor: string = '#f3f3f3';
  public footerColor: string = '#dedede';
  public footerTextColor: string = '#000000';
  public borderColor: string = '#d3d3d3';
  public textColor: string = '#000000';
  public activatedTheme:number = 0;
  public borderRadius:boolean = false;
  public selectedBorderStyle:number = 1;
  public tableBlock: Block;
  public role_id: number = 0;
  public borderStyle:Array<BorderStyle> = [
    { id: 1, name: this._translocoService.translate("Content table settings Table border solid text (Template/Document - section) - Solid") },
    { id: 2, name: this._translocoService.translate("Content table settings Table border dotted (Template/Document - section) - Dotted") }
  ];
  public tableUUid:number|string = 0;
  public currentElement:HTMLElement = null;
  private editor:Editor;

  constructor(
    private eventHandlersService: EventHandlersService,
    private globalService: GlobalService,
    private generalPopoverSettings: GeneralPopoverSettingsComponent,
    rendererFactory: RendererFactory2,
    private _translocoService:TranslocoService
  ) { this.renderer = rendererFactory.createRenderer(null, null); }

  async onInit() {
    this.tableUUid = this.data['uuid'];
    this.initEditor(this.tableUUid);
    this.tableBlock = this.globalService.fields_data['blocks'][this.tableUUid];
    this.colors = this.colorWithHeader;
    this.currentElement = document.querySelector("[editor-uuid='" + this.tableUUid + "'] table");
    this.header = this.tableBlock?.setting?.has_header == "1" ? true : false;
    this.footer = this.tableBlock?.setting?.has_footer == "1" ? true : false;
    this.activatedTheme = this.tableBlock?.setting?.activated_theme;
    this.selectedBorderStyle = Number(this.tableBlock?.setting?.border_style);
    this.headerColor = this.tableBlock?.setting?.header_color;
    this.headerTextColor = this.tableBlock?.setting?.header_text_color ?? "#000000";
    this.evenColor = this.tableBlock?.setting?.even_color;
    this.oddColor = this.tableBlock?.setting?.odd_color;
    this.footerColor = this.tableBlock?.setting?.footer_color;
    this.footerTextColor = this.tableBlock?.setting?.footer_text_color ?? "#000000";
    this.borderColor = this.tableBlock?.setting?.border_color;
    this.borderRadius = this.tableBlock?.setting?.border_radius == "1" ? true : false;
    this.textColor = this.tableBlock?.setting?.text_color;
    this.role_id = this.tableBlock?.role_id ?? 0;
    await this.generalPopoverSettings.setupAllFields();
  }

  initEditor(uuid){
    const editor = document.querySelector(`.editor${uuid}`) as HTMLElement;
    if(editor){
      const alreadyEditor = editor.querySelector('.tiptap');
      if(!alreadyEditor){
        this.globalService.initEditor({uuid:uuid});
      }
    }
    this.editor = this.globalService.editorInstances[this.tableUUid];
  }

  getTableContent():HTMLElement{
    const createElement = document.createElement('div');
    createElement.innerHTML = this.globalService.editorInstances[this.tableUUid].getHTML();
    return createElement;
  }

  /**
   * detect the component changes
   * @param changes 
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.data = changes['data'].currentValue;
      this.onInit();
    }
  }


  /**
   * back to content box setting
   */
  backToContentSetting(){
    this.eventHandlersService.triggerEditorSideMenu({uuid:this.data['uuid']}, "CONTENTBOXSETTINGMENU");
  }

  /**
   * close the content table right menu
   */
  clsoe() {
    this.eventHandlersService.triggerEditorSideMenu({}, 'DEFAULT');
  }

  /**
   * toggle the header color
   * @param event 
   */
  toggleHeaderColor(event:MouseEvent) {
    const elemenet = event.target as HTMLInputElement
    this.header = elemenet.checked
    if (elemenet.checked) {
      this.colors = this.colorWithHeader;
    } else {
      this.colors = this.colorWithOutHeader;
    }
    this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{has_header:(this.header ? 1 : 0)}}});
    this.changeTableTheme(this.colors[this.activatedTheme]);
  }

  /**
   * toggle the footer color
   * @param event 
   */
  toggleFooterColor(event:MouseEvent) {
    const elemenet = event.target as HTMLInputElement
    this.footer = elemenet.checked;
    this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{has_footer:(this.footer ? 1 : 0)}}});
    this.changeTableTheme(this.colors[this.activatedTheme]);
  }

  /**
   * select the table theme
   * @param options 
   * @param index 
   */
  selectTheme(options: Theme, index: number) {
    this.activatedTheme = index;
    this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{activated_theme:index}}});
    this.changeTableTheme(options);
  }

  /**
   * change the table theme
   * @param options 
   */
  changeTableTheme(options:Theme) {
    const createElement = this.getTableContent();
    if (this.activatedTheme > -1) {
      if (this.header) {
        createElement.querySelector('table').querySelector('.table_headingsRow').classList.remove('hidden');
        createElement.querySelector('table').style.setProperty("--header-color",options?.headerColor);
        createElement.querySelector('table').style.setProperty("--even-color",options?.evenColor);
        createElement.querySelector('table').style.setProperty("--odd-color",options?.oddColor);
        this.headerColor = options?.headerColor;
        this.evenColor = options?.evenColor;
        this.oddColor = options?.oddColor;
        this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{header_color:options?.headerColor,even_color:options?.evenColor,odd_color:options?.oddColor}}});
      } else {
        createElement.querySelector('table').querySelector('.table_headingsRow').classList.add('hidden');
        createElement.querySelector('table').style.setProperty("--even-color",options?.oddColor);
        createElement.querySelector('table').style.setProperty("--odd-color",options?.evenColor);
        this.evenColor = options?.oddColor;
        this.oddColor = options?.evenColor;
        this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{even_color:options?.oddColor,odd_color:options?.evenColor}}});
      }

      if (this.footer) {
        const lastRow = createElement.querySelector('table').rows.length - 1;
        createElement.querySelector('table').rows[lastRow].classList.add('table-footer');
        createElement.querySelector('table').rows[lastRow].classList.remove('hidden');
        createElement.querySelector('table').rows[lastRow].style.setProperty("color",this.footerTextColor);
        createElement.querySelector('table').style.setProperty("--footer-color",options?.secondaryFooterColor);
        this.footerColor = options?.secondaryFooterColor;
        this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{secondary_footer_color:options?.secondaryFooterColor}}});
      }else{
        const lastRow = createElement.querySelector('table').rows.length - 1;
        createElement.querySelector('table').rows[lastRow].classList.remove('table-footer');
        createElement.querySelector('table').rows[lastRow].classList.add('hidden');
        createElement.querySelector('table').rows[lastRow].style.setProperty("color",this.footerTextColor);
        createElement.querySelector('table').style.setProperty("--footer-color",options?.secondaryFooterColor);
        this.footerColor = options?.secondaryFooterColor;
        this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{secondary_footer_color:options?.secondaryFooterColor}}});
      }
      this.editor.commands.setContent(createElement.innerHTML);
    } else {
      if (this.header) {
        createElement.querySelector('table').querySelector('.table_headingsRow').classList.remove('hidden');
        createElement.querySelector('table').style.setProperty("--header-color",this.headerColor);
        createElement.querySelector('table').style.setProperty("--even-color",this.evenColor);
        createElement.querySelector('table').style.setProperty("--odd-color",this.oddColor);
        this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{header_color:this.headerColor,even_color:this.evenColor,'odd-color':this.oddColor}}});
      } else {
        createElement.querySelector('table').querySelector('.table_headingsRow').classList.add('hidden');
        createElement.querySelector('table').style.setProperty("--even-color",options?.evenColor);
        createElement.querySelector('table').style.setProperty("--odd-color",options?.oddColor);
        this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{even_color:this.evenColor,odd_color:this.oddColor}}});
      }
      if (this.footer) {
        const lastRow = createElement.querySelector('table').rows.length - 1;
        createElement.querySelector('table').rows[lastRow].classList.add('table-footer');
        createElement.querySelector('table').rows[lastRow].classList.remove('hidden');
        createElement.querySelector('table').rows[lastRow].style.setProperty("color",this.footerTextColor);
        createElement.querySelector('table').style.setProperty("--footer-color",this.footerColor);
        this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{secondary_footer_color:this.footerColor}}});
      }else{
        const lastRow = createElement.querySelector('table').rows.length - 1;
        createElement.querySelector('table').rows[lastRow].classList.remove('table-footer');
        createElement.querySelector('table').rows[lastRow].classList.add('hidden');
        createElement.querySelector('table').rows[lastRow].style.setProperty("color",this.footerTextColor);
        createElement.querySelector('table').style.setProperty("--footer-color",this.footerColor);
        this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{secondary_footer_color:this.footerColor}}});
      }
      this.editor.commands.setContent(createElement.innerHTML);
    }
  }

  /**
   * change the header color
   * @param color 
   */
  changeHeaderColor(color: string) {
    this.activatedTheme = -1;
    const createElement = this.getTableContent();
    createElement.querySelector('table').style.setProperty('--header-color',color);
    this.editor.commands.setContent(createElement.innerHTML);
    this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{header_color: color, activated_theme: -1 }}});
  }

  /**
   * change the header color
   * @param color 
   */
  changeHeaderTextColor(color: string) {
    const createElement = this.getTableContent();
    createElement.querySelector('table tr.table_headingsRow')["style"].setProperty('color',color);
    this.editor.commands.setContent(createElement.innerHTML);
    this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{header_text_color: color }}});
  }

  /**
   * change the row even color
   * @param color 
   */
  changeEvenColor(color: string) {
    this.activatedTheme = -1;
    const createElement = this.getTableContent();
    createElement.querySelector('table').style.setProperty('--even-color',color);
    this.editor.commands.setContent(createElement.innerHTML);
    this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{even_color: color, activated_theme: -1 }}});
  }

  /**
   * change the row odd color
   * @param color 
   */
  changeOddColor(color: string) {
    this.activatedTheme = -1;
    const createElement = this.getTableContent();
    createElement.querySelector('table').style.setProperty('--odd-color',color);
    this.editor.commands.setContent(createElement.innerHTML);
    this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{ odd_color: color, activated_theme: '-1' }}});
  }

  /**
   * change the table footer color
   * @param color 
   */
  changeFooterColor(color: string) {
    this.activatedTheme = -1;
    const createElement = this.getTableContent();
    createElement.querySelector('table').style.setProperty('--footer-color',color);
    this.editor.commands.setContent(createElement.innerHTML);
    this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{ footer_color: color, activated_theme: '-1' , secondary_footer_color:color}}});
  }

  /**
   * change the footer text color
   * @param color 
   */
  changeFooterTextColor(color: string) {
    const createElement = this.getTableContent();
    const lastRow = createElement.querySelector('table').rows.length - 1;
    createElement.querySelector('table').rows[lastRow].style.setProperty("color",this.footerTextColor);
    this.editor.commands.setContent(createElement.innerHTML);
    this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{ footer_text_color: color}}});
  }

  /**
   * change the table border color
   * @param color 
   */
  changeBorderColor(color: string) {
    const createElement = this.getTableContent();
    createElement.querySelector('table').style.setProperty("border-color",color);
    createElement.querySelectorAll('table th').forEach((th:HTMLElement) => {
        th.style.setProperty("border-color",color);
    });
    createElement.querySelectorAll('table td').forEach((td:HTMLElement) => {
        td.style.setProperty("border-color",color);
    });
    this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{ border_color:color}}});
    if (this.borderRadius) {
      createElement.querySelector('table').style.setProperty("box-shadow",'0 0 0 1px ' + color);
    }
    this.editor.commands.setContent(createElement.innerHTML);
  }

  /**
   * change the table text color
   * @param color 
   */
  changeTextColor(color: string) {
    const createElement = this.getTableContent();
    createElement.querySelector('table').style.setProperty("color",color);
    this.editor.commands.setContent(createElement.innerHTML);
    this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{ text_color:color}}});
  }

  /**
   * change the table border color
   * @param style 
   */
  changeBorderStyle(style: BorderStyle) {
    const createElement = this.getTableContent();
    createElement.querySelector('table').setAttribute("border-style",String(style.id));
    this.editor.commands.setContent(createElement.innerHTML);
    this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{ border_style:style.id}}});
  }

  /**
   * toggle the table border radius
   * @param event 
   */
  toggleBorderRadius(event: MouseEvent) {
    const elemenet = event.target as HTMLInputElement;
    this.borderRadius = elemenet.checked;
    const createElement = this.getTableContent();
    if(this.borderRadius){
      createElement.querySelector('table').classList.add('table-border-radius');
    }else{
      createElement.querySelector('table').classList.remove('table-border-radius');
    }
    createElement.querySelector('table').setAttribute("border-radius", (this.borderRadius ? '1' : '0'));
    if(this.tableBlock?.setting){
      this.tableBlock = this.globalService.updateBlock(this.tableUUid, {setting:{...this.tableBlock.setting, ...{ border_radius:(this.borderRadius ? '1' : '0')}}});
    }
    this.editor.commands.setContent(createElement.innerHTML);
  }

  /**
  * select role for allow edit on signing area 
  */
  selectRole(role: SelectedRole) {
    this.tableBlock = this.globalService.updateBlock(this.tableUUid, { role_id: role.id });
  }

  onLockChange(checked:boolean) {
    this.editor.setEditable(!checked);
  }

}
